import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import backend from '@api/backend'
import {
  IBillingEntityRaw,
  IBillingEntity,
  IBillingEntityLocation,
  IPortalSession,
  IStripePriceRow,
  CreateBillingEntityData,
  OpenPortalSessionData,
  SharePaymentLinkData,
  IBillingApiError,
} from './model/billing.model'
import {
  notifyApiError,
  notifyError,
  notifySuccess,
} from '@common/utils/notify'
import axios from 'axios'

/** Extract the structured error body the backend returns for billing failures. */
export function getBillingApiError(error: unknown): IBillingApiError | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.data as IBillingApiError | undefined
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

function transformBillingEntity(raw: IBillingEntityRaw): IBillingEntity {
  return {
    id: raw.id,
    accountId: raw.account_id,
    stripeAccountId: raw.stripe_account_id,
    stripeCustomerId: raw.stripe_customer_id,
    name: raw.name,
    email: raw.email,
    status: raw.status,
    requiresIndividualPoPerInvoice: raw.requires_individual_po_per_invoice,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const useBillingEntities = (accountId: number | undefined) => {
  return useQuery({
    queryKey: ['billing-entities', accountId],
    queryFn: () => getBillingEntities(accountId!),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 15,
  })
}

export const useBillingEntity = (billingEntityId: number | undefined) => {
  return useQuery({
    queryKey: ['billing-entity', billingEntityId],
    queryFn: () => getBillingEntity(billingEntityId!),
    enabled: !!billingEntityId,
    staleTime: 1000 * 60 * 15,
  })
}

export const usePortalSession = (billingEntityId: number | undefined, returnUrl: string) => {
  return useQuery({
    queryKey: ['portal-session', billingEntityId],
    queryFn: () => getPortalSession(billingEntityId!, returnUrl),
    enabled: false, // Only fetched on demand via refetch()
    staleTime: 0,   // Portal sessions are short-lived — never cache
  })
}

/**
 * Opens the Stripe billing portal for whichever billing entity the user picked.
 * A mutation rather than a query because the entity isn't known until click —
 * `usePortalSession` has to bind its ID up front, so it can't serve a list.
 */
export const useOpenPortalSession = () => {
  return useMutation({
    mutationFn: ({ billingEntityId, returnUrl }: OpenPortalSessionData) =>
      getPortalSession(billingEntityId, returnUrl),
    onSuccess: (session: IPortalSession) => {
      // Stripe portal URLs are single-use and short-lived, so navigate straight
      // away rather than holding the URL in state.
      window.open(session.url, '_blank', 'noopener,noreferrer')
    },
    onError: (error) => {
      notifyApiError(error, 'Failed to open billing portal')
    },
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export const useCreateBillingEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBillingEntity,
    onSuccess: (data: IBillingEntity) => {
      queryClient.setQueryData(
        ['billing-entities', data.accountId],
        (old: IBillingEntity[] | undefined) => [data, ...(old ?? [])],
      )
      notifySuccess('Billing entity created successfully')
    },
    onError: (error) => {
      // Field-specific errors (e.g. an invalid VAT number) are shown inline on
      // the form by the modal — don't also raise a toast for them.
      if (getBillingApiError(error)?.field) return
      notifyApiError(error, 'Failed to create billing entity')
    },
  })
}

export const useDeleteBillingEntity = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteBillingEntity,
    onSuccess: (_data, billingEntity) => {
      queryClient.setQueryData(
        ['billing-entities', billingEntity.accountId],
        (old: IBillingEntity[] | undefined) => (old ?? []).filter((be) => be.id !== billingEntity.id),
      )
      notifySuccess('Billing entity deleted successfully')
    },
    onError: (error) => {
      notifyApiError(error, 'Failed to delete billing entity')
    },
  })
}

export const useSharePaymentLink = () => {
  return useMutation({
    mutationFn: sharePaymentLink,
    onSuccess: () => {
      notifySuccess('Payment link sent successfully')
    },
    onError: () => {
      notifyError('Failed to send payment link', {
        autoClose: 5000,
      })
    },
  })
}

// ---------------------------------------------------------------------------
// Async functions
// ---------------------------------------------------------------------------

async function getBillingEntities(accountId: number): Promise<IBillingEntity[]> {
  const response = await backend.get('/billing_entities', { params: { account_id: accountId } })
  return (response.data as IBillingEntityRaw[]).map(transformBillingEntity)
}

async function getBillingEntity(billingEntityId: number): Promise<IBillingEntity> {
  const response = await backend.get(`/billing_entities/${billingEntityId}`)
  return transformBillingEntity(response.data as IBillingEntityRaw)
}

async function getPortalSession(billingEntityId: number, returnUrl: string): Promise<IPortalSession> {
  const response = await backend.post(`/billing_entities/${billingEntityId}/portal_session`, null, {
    params: { return_url: returnUrl },
  })
  return response.data as IPortalSession
}

async function createBillingEntity(data: CreateBillingEntityData): Promise<IBillingEntity> {
  const response = await backend.post('/billing_entities', data)
  return transformBillingEntity(response.data as IBillingEntityRaw)
}

async function deleteBillingEntity(billingEntity: IBillingEntity): Promise<void> {
  await backend.delete(`/billing_entities/${billingEntity.id}`)
}

async function sharePaymentLink(data: SharePaymentLinkData): Promise<void> {
  await backend.post(`/billing_entities/${data.billing_entity_id}/share_payment_link`, data)
}

export const useBillingEntityPrices = (billingEntityId: number | undefined) => {
  return useQuery({
    queryKey: ['billing-entity-prices', billingEntityId],
    queryFn: () => getBillingEntityPrices(billingEntityId!),
    enabled: !!billingEntityId,
    staleTime: 1000 * 60 * 15,
  })
}

async function getBillingEntityPrices(billingEntityId: number): Promise<IStripePriceRow[]> {
  const response = await backend.get(`/billing_entities/${billingEntityId}/prices`)
  return response.data as IStripePriceRow[]
}

export const useBillingEntityLocations = (billingEntityId: number | undefined) => {
  return useQuery({
    queryKey: ['billing-entity-locations', billingEntityId],
    queryFn: () => getBillingEntityLocations(billingEntityId!),
    enabled: !!billingEntityId,
    staleTime: 1000 * 60 * 5,
  })
}

async function getBillingEntityLocations(billingEntityId: number): Promise<IBillingEntityLocation[]> {
  const response = await backend.get(`/billing_entities/${billingEntityId}/locations`)
  return response.data as IBillingEntityLocation[]
}
